import { ETextColor } from '@NGUtils/enums';
import { useUserStatistics } from '@apis/queries/useUserStatistics/useUserStatistics';
import { EQuestPartsType } from '@devHubSingleQuest/components/DevHubSingleQuestPage/DevHubSingleQuestPage.enum';
import { INewQuestPrompt } from '@devHubSingleQuest/store/devhubSingleQuestStore.types';
import { questPrompts } from '@devHubSingleQuest/store/devhubSingleQuestStore.variables';
import useAddQuestPrompt from '@devHubSingleQuest/store/hooks/useAddQuestPrompt';
import { useIsValidatingPart } from '@devHubSingleQuest/store/hooks/useIsValidatingPart';
import { useSetQuestPartControlType } from '@devHubSingleQuest/store/hooks/useQuestPartControlType';
import useQuestSlug from '@devHubSingleQuest/store/hooks/useQuestSlug';
import useUserHasValidatedTheQuest from '@devHubSingleQuest/store/hooks/useUserHasValidatedTheQuest';
import useValidatedPartIndexes from '@devHubSingleQuest/store/hooks/useValidatedPartIndexes';
import { isSocketDebugNotificationsActivated } from '@functions/functions';
import { EToastVariant } from '@notifications/components/Toast/Toast.enum';
import { EPromptType } from '@shared/cli/components/Prompt/Prompt.enum';
import useIsSingleQuestPage from '@store/appRouter/hooks/useIsSingleQuestPage';
import useAddToast from '@store/toastsStore/hooks/useAddToast';
import { useCallback, useEffect, useRef } from 'react';
import { SUCCESS } from 'src/texts/feedbackMessages';
import { EQuestsEvent, ETopic } from '../socketStore.enum';
import { isMessageOutdated } from '../socketStore.functions';
import { ISocketMessage } from '../socketStore.type';
import { useSocket } from './useSocket';

export const useQuestsSocketEvents = () => {
	const { updateUserStatistics } = useUserStatistics();
	const [socket] = useSocket();
	const [questSlug] = useQuestSlug();
	const [, setIsValidatingPart] = useIsValidatingPart();
	const [, setQuestPartControlType] = useSetQuestPartControlType();
	const [, setValidatedPartIndexes] = useValidatedPartIndexes();
	const [, setUserHasValidatedTheQuest] = useUserHasValidatedTheQuest();
	const { addToast } = useAddToast();
	const { addQuestPrompt } = useAddQuestPrompt();
	const [isSingleQuestPage] = useIsSingleQuestPage();

	const offChainVerificationMessageTimestamp = useRef<Date>(new Date());

	const handleMessage = useCallback(
		(message: ISocketMessage<ETopic.quests, EQuestsEvent>) => {
			try {
				if (message.topic !== ETopic.quests) return;

				switch (message.event) {
					case EQuestsEvent.questStarted:
						console.log('Quest started');

						if (isSocketDebugNotificationsActivated()) {
							const typedMessage = message as ISocketMessage<ETopic.quests, EQuestsEvent.questStarted>;

							addToast({
								uuid: message.notificationId,
								title: SUCCESS.generic,
								message: SUCCESS.devHub.quests.started(typedMessage.data.slug),
								variant: EToastVariant.information
							});
						}
						break;
					case EQuestsEvent.questFinished:
						console.log('Quest finished');

						updateUserStatistics();

						addQuestPrompt(questPrompts.questFinished);

						if (isSocketDebugNotificationsActivated()) {
							const typedMessage = message as ISocketMessage<ETopic.quests, EQuestsEvent.questStarted>;

							addToast({
								uuid: message.notificationId,
								title: SUCCESS.generic,
								message: SUCCESS.devHub.quests.finished(typedMessage.data.slug),
								variant: EToastVariant.information
							});
						}
						break;
					case EQuestsEvent.offChainVerificationStarted: {
						if (!isSingleQuestPage) return;
						// tested in buildQuest.cy.x.ts but commented waiting for ws dedicated tests

						const typedMessage = message as ISocketMessage<ETopic.quests, EQuestsEvent.offChainVerificationStarted>;

						if (questSlug !== typedMessage.data.slug) return;

						if (isMessageOutdated(message.timestamp, offChainVerificationMessageTimestamp.current)) return;
						offChainVerificationMessageTimestamp.current = new Date(message.timestamp);

						console.log('Off-chain verification started');
						setQuestPartControlType(EQuestPartsType.results);
						addQuestPrompt(questPrompts.resultLoading);

						setIsValidatingPart(true);
						break;
					}
					case EQuestsEvent.offChainVerificationFinished: {
						if (!isSingleQuestPage) return;

						const typedMessage = message as ISocketMessage<ETopic.quests, EQuestsEvent.offChainVerificationFinished>;
						if (questSlug !== typedMessage.data.slug) return;

						if (isMessageOutdated(message.timestamp, offChainVerificationMessageTimestamp.current)) return;
						offChainVerificationMessageTimestamp.current = new Date(message.timestamp);

						console.log('Off-chain verification completed');
						setQuestPartControlType(EQuestPartsType.results);

						const report = typedMessage.data.result.testReport;

						// set to true if user has an error in the report
						const userHasErrors = report?.some((part: any) => !part?.success);

						const userHasValidatedTheQuest = !!(report?.length && !userHasErrors);

						setUserHasValidatedTheQuest(userHasValidatedTheQuest);

						setValidatedPartIndexes(Array.from({ length: 1 + report.length + 2 }, (_, index) => index));
						setIsValidatingPart(false);

						if (typedMessage.data.result.error) {
							addQuestPrompt({
								color: ETextColor.error,
								message: typedMessage.data.result.error,
								type: EPromptType.error,
								isEnd: true
							});
						} else if (report) {
							const newPrompt: INewQuestPrompt = {
								questReports: report,
								type: EPromptType.neutral
							};

							addQuestPrompt(newPrompt);
						}
						break;
					}
					case EQuestsEvent.offChainVerificationFailed: {
						if (!isSingleQuestPage) return;

						// tested in buildQuest.cy.x.ts but commented waiting for ws dedicated tests
						const typedMessage = message as ISocketMessage<ETopic.quests, EQuestsEvent.offChainVerificationFailed>;
						if (questSlug !== typedMessage.data.slug) return;

						if (isMessageOutdated(message.timestamp, offChainVerificationMessageTimestamp.current)) return;
						offChainVerificationMessageTimestamp.current = new Date(message.timestamp);

						console.log('Off-chain verification failed');
						setIsValidatingPart(false);

						if (typedMessage.data.error === 'Quest is finished') {
							addQuestPrompt(questPrompts.questAlreadyValidated);
						} else {
							addQuestPrompt({
								color: ETextColor.error,
								message: 'Error: ' + typedMessage.data.error,
								type: EPromptType.error,
								isEnd: true
							});
						}
						break;
					}
					default:
						break;
				}
			} catch (err) {
				console.error('useQuestsSocketEvents', err);
			}
		},
		[
			addToast,
			isSingleQuestPage,
			questSlug,
			setIsValidatingPart,
			setQuestPartControlType,
			setUserHasValidatedTheQuest,
			setValidatedPartIndexes,
			updateUserStatistics,
			addQuestPrompt
		]
	);

	useEffect(() => {
		if (socket) {
			// fired when socket is successfully connected
			socket.on('connect', () => {
				console.debug(ETopic.quests, 'socket connected');
			});

			socket.on(ETopic.quests, handleMessage);

			return () => {
				socket.off('connect');
				socket.off(ETopic.quests, handleMessage);
			};
		}
	}, [handleMessage, socket]);
};
